<template>
  <vee-form v-slot="{ validate }" as="" :initial-errors="initialErrors">
    <form
      ref="formRef"
      class="standard-form"
      method="post"
      action="/my/account/login"
      novalidate
      @submit.prevent="handleSubmit(validate)"
    >
      <input name="_method" type="hidden" value="PUT" />
      <input
        id="update_blender_login"
        type="hidden"
        name="update_blender_login"
        value="update_blender_login"
      />

      <text-input
        v-model="formData.currentPassword"
        input-id="current_password"
        type="password"
        label="Current Password"
        placeholder="Current Password"
        rules="required"
        validate-as="Current password"
        autocomplete="current-password"
      >
        <template #note>
          <p class="small">
            To change your Blender login username or password, start by entering your current
            password
          </p>
        </template>
      </text-input>

      <text-input
        v-model="user.username"
        input-id="username"
        label="Username"
        placeholder="Username"
        rules="required|min:4|alpha_dash:en|fbUsername|fbUsernameUnique"
        autocomplete="username"
      />

      <text-input
        v-model="formData.newPassword"
        input-id="password"
        type="password"
        label="New Password"
        placeholder="New Password"
        rules="min:12|fbNotSameAs:username|fbPasswordStrengthRule"
        validate-as="New password"
        autocomplete="new-password"
      />

      <text-input
        v-model="formData.confirmPassword"
        input-id="password_confirmation"
        type="password"
        label="Confirm New Password"
        placeholder="Confirm New Password"
        rules="confirmed:@password,password"
        validate-as="Confirm new password"
        autocomplete="new-password"
      />

      <div class="action__group form-actions">
        <csrf-input />

        <div class="form-action">
          <submit-button
            :submitting="processing"
            label="Update"
            submitting-label="Processing…"
            classes="-main -large-medium-only"
            @submit-action="handleSubmit(validate)"
          />
        </div>
      </div>
    </form>
  </vee-form>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapWritableState } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { Form as VeeForm, type PrivateFormContext } from 'vee-validate'
import { errorBagToInitialErrors, initExtraValidationRules } from '@/utils/forms/validation-rules'
import TextInput from '@/components/forms/text-input.vue'
import CsrfInput from '@/components/forms/csrf-input.vue'
import SubmitButton from '@/components/buttons/submit-button.vue'

/**
 * "blender" auth registration from
 * - if showAside, lots of container classes change to accommodate /join styling
 * - otherwise see organization join, which is much more simple
 *
 * Side effect is empty div container classes
 */
export default defineComponent({
  name: 'EditLoginForm',
  components: {
    VeeForm,
    TextInput,
    CsrfInput,
    SubmitButton,
  },
  props: {
    errorBag: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      processing: false,
      formData: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
    }
  },
  computed: {
    ...mapWritableState(useUserAuthStore, ['user']),

    // only return first error per field, backend returns array
    initialErrors(): Record<string, string> {
      return errorBagToInitialErrors(this.errorBag)
    },
  },

  // lifecycle methods
  created() {
    // load custom fb* validation rules
    initExtraValidationRules()
  },

  // instance methods
  methods: {
    async handleSubmit(validate: PrivateFormContext['validate']) {
      if (this.processing) {
        return
      }

      // disable submit
      this.processing = true

      try {
        const results = await validate()

        // invalid, stop
        if (!results.valid) {
          this.processing = false
          return
        }

        // traditional form submit
        if (this.$refs.formRef instanceof HTMLFormElement) {
          // wait until next tick to make sure v-model update
          await this.$nextTick()
          this.$refs.formRef.submit()
        }
      } catch {
        // if error thrown, enable form
        this.processing = false
      }
    },
  },
})
</script>
